import { useState, useEffect } from 'react';
import {
  Key, Link as LinkIcon, Save, Eye, EyeOff, Lock,
  Mail, Facebook, Instagram, Smartphone, Calendar, ExternalLink, UserCheck
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getSettings, updateSettings } from '@/lib/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    admin_email: '', brevo_api_key: '',
    calendly_intro_link: '', calendly_mastery_link: '',
    whop_link: '', stripe_pk: '', stripe_sk: '',
    facebook_page: '', instagram_handle: '',
    sms_phone: '', sms_carrier: '', webhook_verify_token: '',
    brevo_webhook_secret: '',
    checkr_api_key: '', checkr_package_slug: 'basic_criminal',
  });
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [showCheckrKey, setShowCheckrKey] = useState(false);

  useEffect(() => {
    getSettings().then((s) => {
      if (s) setSettings(prev => ({ ...prev, ...s }));
    }).catch(() => toast.error('Failed to load settings')).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...settings };
      if (newPassword) data.admin_password = newPassword;
      delete data.id;
      delete data.updated_at;
      await updateSettings(data);
      toast.success('Settings saved');
      setNewPassword('');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <AdminLayout title="Settings"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  const Section = ({ icon: Icon, title, desc, children }) => (
    <div className="p-6 bg-white/[0.04] border border-white/[0.06] rounded-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-rtb-purple/10 flex items-center justify-center rounded-lg">
          <Icon className="w-5 h-5 text-rtb-purple" />
        </div>
        <div>
          <h3 className="font-oswald text-lg font-bold text-white">{title}</h3>
          {desc && <p className="text-white/40 text-sm">{desc}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );

  const Field = ({ label, value, onChange, type = "text", placeholder = "", hidden = false, toggleHidden, testId }) => (
    <div className="relative">
      <Label className="text-white/60 mb-2 block">{label}</Label>
      <Input
        type={hidden ? "password" : type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input-dark"
        placeholder={placeholder}
        data-testid={testId}
      />
      {toggleHidden !== undefined && (
        <button type="button" onClick={toggleHidden} className="absolute right-3 top-[38px] text-white/40 hover:text-white">
          {hidden ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
        </button>
      )}
    </div>
  );

  return (
    <AdminLayout title="Settings">
      <div className="space-y-6">
        <Section icon={Calendar} title="Calendly Links" desc="Session booking links for participants">
          <Field label="Introduction Call (30 min)" value={settings.calendly_intro_link}
            onChange={v => setSettings({ ...settings, calendly_intro_link: v })}
            placeholder="https://calendly.com/..." testId="calendly-intro" />
          <Field label="Mastery Session (40 min, Wed & Sat)" value={settings.calendly_mastery_link}
            onChange={v => setSettings({ ...settings, calendly_mastery_link: v })}
            placeholder="https://calendly.com/..." testId="calendly-mastery" />
        </Section>

        <Section icon={ExternalLink} title="Payment Links" desc="Whop and Stripe configuration">
          <Field label="Whop Page URL ($75 Base Program)" value={settings.whop_link}
            onChange={v => setSettings({ ...settings, whop_link: v })}
            placeholder="https://whop.com/..." testId="whop-link" />
          <Field label="Stripe Publishable Key ($400 Mastermind)" value={settings.stripe_pk}
            onChange={v => setSettings({ ...settings, stripe_pk: v })}
            placeholder="pk_..." testId="stripe-pk" />
          <Field label="Stripe Secret Key" value={settings.stripe_sk}
            onChange={v => setSettings({ ...settings, stripe_sk: v })}
            placeholder="sk_..." hidden={!showStripeKey}
            toggleHidden={() => setShowStripeKey(!showStripeKey)} testId="stripe-sk" />
        </Section>

        <Section icon={Mail} title="Email & SMS (Brevo)" desc="Brevo API for transactional email and SMS notifications">
          <Field label="Admin Email (receives notifications)" value={settings.admin_email}
            onChange={v => setSettings({ ...settings, admin_email: v })}
            type="email" placeholder="admin@example.com" testId="admin-email" />
          <Field label="Custom Email Address (sends replies)" value={settings.custom_email}
            onChange={v => setSettings({ ...settings, custom_email: v })}
            type="email" placeholder="support@readytobuildit.life" testId="custom-email" />
          <Field label="Brevo API Key" value={settings.brevo_api_key}
            onChange={v => setSettings({ ...settings, brevo_api_key: v })}
            placeholder="xkeysib-..." hidden={!showBrevoKey}
            toggleHidden={() => setShowBrevoKey(!showBrevoKey)} testId="brevo-key" />
          <div className="flex items-center justify-between p-4 bg-white/[0.03] rounded-lg border border-white/[0.06]">
            <div>
              <p className="text-white/80 text-sm font-medium">AI Auto-Respond</p>
              <p className="text-white/30 text-xs">Automatically reply to common questions. Complex emails are flagged for review.</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, email_auto_respond: !settings.email_auto_respond })}
              data-testid="settings-auto-respond-toggle"
            >
              {settings.email_auto_respond ? (
                <div className="w-12 h-6 bg-emerald-500 rounded-full relative transition-colors">
                  <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-all" />
                </div>
              ) : (
                <div className="w-12 h-6 bg-white/10 rounded-full relative transition-colors">
                  <div className="w-5 h-5 bg-white/40 rounded-full absolute left-0.5 top-0.5 transition-all" />
                </div>
              )}
            </button>
          </div>
        </Section>

        <Section icon={Smartphone} title="SMS Notifications" desc="SMS alerts via Brevo transactional SMS">
          <Field label="Your Phone Number (with country code)" value={settings.sms_phone}
            onChange={v => setSettings({ ...settings, sms_phone: v })}
            type="tel" placeholder="+1 (555) 000-0000" testId="sms-phone" />
        </Section>

        <Section icon={Facebook} title="Social Media">
          <Field label="Facebook Page" value={settings.facebook_page}
            onChange={v => setSettings({ ...settings, facebook_page: v })} testId="facebook-page" />
          <Field label="Instagram Handle" value={settings.instagram_handle}
            onChange={v => setSettings({ ...settings, instagram_handle: v })} testId="instagram-handle" />
        </Section>

        <Section icon={Key} title="Meta Webhook Integration" desc="Connect Facebook/Instagram for automatic lead capture">
          <Field label="Verify Token" value={settings.webhook_verify_token}
            onChange={v => setSettings({ ...settings, webhook_verify_token: v })}
            placeholder="your_verify_token" testId="webhook-token" />
          <div>
            <Label className="text-white/60 mb-2 block">Webhook Callback URL (copy to Meta)</Label>
            <div className="flex items-center gap-2">
              <Input value={`${process.env.REACT_APP_BACKEND_URL}/api/webhook/meta`} readOnly className="input-dark text-white/60 text-sm" />
              <button onClick={() => { const el = document.createElement('textarea'); el.value = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/meta`; el.style.position = 'fixed'; el.style.opacity = '0'; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); toast.success('Copied!'); }}
                className="px-3 py-2 bg-rtb-purple/10 text-rtb-purple text-xs rounded-lg hover:bg-rtb-purple/20">Copy</button>
            </div>
            <p className="text-white/20 text-xs mt-2">Paste this URL in your Meta App's Webhook settings. Use the verify token above.</p>
          </div>
        </Section>

        <Section icon={Lock} title="AI Video Testimonials (HeyGen)" desc="Generate AI avatar testimonial videos">
          <Field label="HeyGen API Key" value={settings.heygen_api_key}
            onChange={v => setSettings({ ...settings, heygen_api_key: v })}
            placeholder="your_heygen_api_key" type="password" testId="heygen-key" />
          <p className="text-white/20 text-xs">Get your API key from <a href="https://app.heygen.com/settings" target="_blank" rel="noopener noreferrer" className="text-rtb-purple underline">app.heygen.com/settings</a></p>
        </Section>

        <Section icon={UserCheck} title="Background Checks (Checkr)" desc="Automated background screening for enrollment">
          <Field label="Checkr Secret API Key" value={settings.checkr_api_key}
            onChange={v => setSettings({ ...settings, checkr_api_key: v })}
            placeholder="your_checkr_secret_key" hidden={!showCheckrKey}
            toggleHidden={() => setShowCheckrKey(!showCheckrKey)} testId="checkr-key" />
          <div>
            <label className="text-white/60 mb-2 block text-sm">Screening Package</label>
            <select
              value={settings.checkr_package_slug || 'basic_criminal'}
              onChange={e => setSettings({ ...settings, checkr_package_slug: e.target.value })}
              className="w-full h-10 bg-white/[0.06] border border-white/[0.08] text-white px-3 rounded-lg focus:border-rtb-purple focus:outline-none appearance-none"
              data-testid="checkr-package"
            >
              <option value="basic_criminal" className="bg-gray-900">Basic Criminal (SSN Trace + National Criminal + Sex Offender)</option>
              <option value="tasker_standard" className="bg-gray-900">Standard (+ County Criminal)</option>
              <option value="driver_pro" className="bg-gray-900">Professional (+ MVR + Education + Employment)</option>
            </select>
          </div>
          <div>
            <label className="text-white/60 mb-2 block text-sm">Checkr Webhook URL (paste in Checkr Dashboard)</label>
            <div className="flex items-center gap-2">
              <input value={`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/checkr`} readOnly className="flex-1 h-10 bg-white/[0.06] border border-white/[0.08] text-white/60 px-3 rounded-lg text-sm" />
              <button onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/checkr`); toast.success('Copied!'); }}
                className="px-3 py-2 bg-rtb-purple/10 text-rtb-purple text-xs rounded-lg hover:bg-rtb-purple/20">Copy</button>
            </div>
            <p className="text-white/20 text-xs mt-2">Get your API key from <a href="https://dashboard.checkr.com/developer" target="_blank" rel="noopener noreferrer" className="text-rtb-purple underline">dashboard.checkr.com/developer</a></p>
          </div>
        </Section>

        <Section icon={Lock} title="Admin Security">
          <Field label="New Admin Password" value={newPassword}
            onChange={setNewPassword} type="password"
            placeholder="Leave blank to keep current" testId="new-password" />
        </Section>

        <Button onClick={handleSave} disabled={saving} className="btn-primary w-full" data-testid="save-settings-btn">
          {saving ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> :
            <><Save className="mr-2 w-5 h-5" /> Save Settings</>}
        </Button>
      </div>
    </AdminLayout>
  );
}
